import PageHeader from '../../components/kit/PageHeader';
import { useState } from 'react';
import { ArrowLeftRight, CreditCard, Plus, ShieldCheck, Scale, ListOrdered, Banknote, Pencil, Percent } from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn, formatVND, formatCompact } from '@/lib/utils';
import Money from '../../components/kit/Money';
import Pill from '../../components/kit/Pill';
import { LONG_TERM_TYPES, hasInterestRate } from '../../../finance';
import { updateSource } from '../../../services/sourceService';
import { useData, useCoreReady } from '../../store/data';
import { useBalanceSheet, useHealth } from '../../store/selectors';
import { useAssetsUI, uid } from './useAssetsUI';
import { useDebtPayments } from './hooks';
import { AdjustSheet, TransferSheet, PayCardSheet, AddWalletSheet, EditWalletSheet } from './WalletSheets';
import { AddLoanSheet, PayDebtSheet, ScheduleSheet, LoanRateSheet, EditLoanSheet } from './LoanSheets';
import ReconcileTab from './ReconcileTab';

type Tab = 'sheet' | 'wallets' | 'loans' | 'reconcile';
const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'sheet', label: 'Bảng cân đối' }, { id: 'wallets', label: 'Ví & thẻ' }, { id: 'loans', label: 'Khoản vay' }, { id: 'reconcile', label: 'Đối soát' }
];
const dm = (d: any) => { const x = d?.toDate ? d.toDate() : d ? new Date(d) : null; return x && !isNaN(x.getTime()) ? `${String(x.getDate()).padStart(2, '0')}/${String(x.getMonth() + 1).padStart(2, '0')}/${x.getFullYear()}` : null; };

// ───────── Bảng cân đối ─────────
function BalanceSheetTab() {
  const s = useBalanceSheet();
  const h = useHealth();
  const Row = ({ label, value, sub, neg }: { label: string; value: number; sub?: string; neg?: boolean }) => (
    <div className="flex items-baseline justify-between gap-3 py-2">
      <div><p>{label}</p>{sub && <p className="text-sm text-muted-foreground">{sub}</p>}</div>
      <Money value={neg ? -value : value} size="row" />
    </div>
  );
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-1"><CardTitle className="text-base font-semibold">Tài sản ròng</CardTitle></CardHeader>
        <CardContent>
          <p className="font-slab num text-4xl font-bold">{formatVND(s.netWorth)}</p>
          <p className="mt-2 max-w-prose text-sm text-muted-foreground">
            = tài sản thanh khoản + tài sản dài hạn − dư nợ thẻ − nợ vay còn lại (kể cả quá hạn). Thẻ tín dụng là nợ, không phải tài sản.
            Tài sản dài hạn (tiết kiệm kỳ hạn, đầu tư, vàng, BĐS…) là giá trị bạn tự khai — nhớ định giá lại định kỳ bằng "Điều chỉnh".
          </p>
        </CardContent>
      </Card>
      <div className="grid gap-4 md:grid-cols-2">
        <Card><CardHeader className="pb-0"><CardTitle className="text-base font-semibold">Tài sản <span className="num font-normal text-muted-foreground">{formatCompact(s.totalAssets)}</span></CardTitle></CardHeader>
          <CardContent className="divide-y divide-border">
            <Row label="Thanh khoản" sub="Tiền mặt, ngân hàng, ví điện tử" value={s.liquid} />
            <Row label="Dài hạn" sub="Tiết kiệm, đầu tư, tài sản" value={s.longTerm} />
          </CardContent></Card>
        <Card><CardHeader className="pb-0"><CardTitle className="text-base font-semibold">Nợ <span className="num font-normal text-muted-foreground">{formatCompact(s.totalLiabilities)}</span></CardTitle></CardHeader>
          <CardContent className="divide-y divide-border">
            <Row label="Thẻ tín dụng" sub="Dư nợ đang dùng" value={s.cardDebt} neg />
            <Row label="Khoản vay" sub="Dư nợ còn lại" value={s.loanDebt} neg />
          </CardContent></Card>
      </div>
      <Card className="flex items-center gap-3 p-4">
        <ShieldCheck size={20} className="shrink-0 text-primary" aria-hidden />
        <div>
          <p className="font-medium">Quỹ khẩn cấp: <span className="num">{formatVND(h.emergencyFund)}</span>
            {h.ratios.emergencyMonths !== null && <span className="text-muted-foreground"> · đủ {h.ratios.emergencyMonths.toFixed(1).replace('.', ',')} tháng chi tiêu</span>}</p>
          <p className="text-sm text-muted-foreground">Đánh dấu ví làm quỹ khẩn cấp ở tab Ví & thẻ; chưa đánh dấu thì tính toàn bộ tiền thanh khoản.</p>
        </div>
      </Card>
    </div>
  );
}

// ───────── Ví & thẻ ─────────
function WalletsTab() {
  const sources = useData(s => s.data.sources).filter(s => s.status !== 'inactive');
  const open = useAssetsUI(s => s.open);
  const groups = [
    { title: 'Thanh khoản', list: sources.filter(s => s.type !== 'credit' && !(LONG_TERM_TYPES as readonly string[]).includes(s.type)) },
    { title: 'Dài hạn', list: sources.filter(s => (LONG_TERM_TYPES as readonly string[]).includes(s.type)) },
    { title: 'Thẻ tín dụng', list: sources.filter(s => s.type === 'credit') }
  ].filter(g => g.list.length);

  const toggleEmergency = async (id: string, on: boolean) => {
    const r: any = await updateSource(uid(), id, { isEmergencyFund: !on });
    r.success ? toast.success(!on ? 'Đã đánh dấu quỹ khẩn cấp' : 'Đã bỏ đánh dấu') : toast.error(r.error ?? 'Không cập nhật được');
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={() => open({ kind: 'transfer' })}><ArrowLeftRight size={14} aria-hidden /> Chuyển khoản</Button>
        <Button variant="outline" size="sm" onClick={() => open({ kind: 'payCard' })}><CreditCard size={14} aria-hidden /> Trả thẻ</Button>
        <Button size="sm" onClick={() => open({ kind: 'addWallet' })}><Plus size={14} aria-hidden /> Thêm ví / thẻ</Button>
      </div>
      {groups.map(g => (
        <section key={g.title}>
          <h2 className="mb-2 px-1 text-base font-semibold">{g.title}</h2>
          <Card className="divide-y divide-border overflow-hidden p-0">
            {g.list.map(w => {
              const credit = w.type === 'credit';
              const used = Number(w.usedCredit) || 0, limit = Number(w.creditLimit) || 0;
              const pct = limit > 0 ? (used / limit) * 100 : 0;
              return (
                <div key={w.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
                  <div className="min-w-0 flex-1 basis-40">
                    <p className="flex items-center gap-1.5 truncate text-base font-medium">{w.name}{w.isEmergencyFund && <ShieldCheck size={13} className="text-primary" aria-label="Quỹ khẩn cấp" />}</p>
                    {credit ? (
                      <div className="mt-1.5 max-w-56">
                        <div className="h-1.5 overflow-hidden rounded-full bg-muted" role="img" aria-label={`Đã dùng ${Math.round(pct)}% hạn mức`}>
                          <div className={cn('h-full rounded-full', pct >= 90 ? 'bg-bad' : pct >= 80 ? 'bg-warn' : 'bg-primary')} style={{ width: `${Math.min(100, pct)}%` }} />
                        </div>
                        <p className="num mt-1 text-sm text-muted-foreground">Hạn mức {formatCompact(limit)}, còn {formatCompact(Math.max(0, limit - used))}{w.paymentDueDay ? `, hạn trả ngày ${w.paymentDueDay}` : ''}{Number(w.interestRate) > 0 ? `, lãi ${w.interestRate}%/năm` : ''}</p>
                      </div>
                    ) : w.valuationDate ? <p className="text-sm text-muted-foreground">Định giá {dm(w.valuationDate)}</p> : null}
                  </div>
                  {credit
                    ? <span className="text-right"><span className="block text-sm text-muted-foreground">Dư nợ</span><Money value={used} size="row" /></span>
                    : <Money value={Number(w.balance) || 0} size="row" className={(Number(w.balance) || 0) < 0 ? 'text-bad' : undefined} />}
                  <div className="flex gap-1">
                    {credit && used > 0 && <Button variant="ghost" size="sm" onClick={() => open({ kind: 'payCard', id: w.id })}>Trả</Button>}
                    {!credit && <Button variant="ghost" size="sm" className="gap-1.5 px-2 sm:w-9 sm:px-0" onClick={() => toggleEmergency(w.id, !!w.isEmergencyFund)} aria-pressed={!!w.isEmergencyFund} aria-label="Đánh dấu quỹ khẩn cấp" title="Quỹ khẩn cấp"><ShieldCheck size={16} className={w.isEmergencyFund ? 'text-primary' : 'text-muted-foreground'} /><span className="sm:hidden">Quỹ khẩn cấp</span></Button>}
                    <Button variant="ghost" size="sm" className="gap-1.5 px-2 sm:w-9 sm:px-0" onClick={() => open({ kind: 'adjust', id: w.id })} aria-label={`Điều chỉnh ${w.name}`} title="Điều chỉnh số dư"><Scale size={16} className="text-muted-foreground" /><span className="sm:hidden">Điều chỉnh</span></Button>
                    <Button variant="ghost" size="sm" className="gap-1.5 px-2 sm:w-9 sm:px-0" onClick={() => open({ kind: 'editWallet', id: w.id })} aria-label={`Sửa ${w.name}`} title="Sửa / ngừng dùng / xoá"><Pencil size={16} className="text-muted-foreground" /><span className="sm:hidden">Sửa</span></Button>
                  </div>
                </div>
              );
            })}
          </Card>
        </section>
      ))}
    </div>
  );
}

// ───────── Khoản vay ─────────
function LoansTab({ payments }: { payments: any[] }) {
  const debts = useData(s => s.data.debts);
  const open = useAssetsUI(s => s.open);
  const live = debts.filter(d => d.type !== 'credit' && (d.status === 'active' || d.status === 'overdue'));
  const paid = debts.filter(d => d.type !== 'credit' && d.status === 'paid');
  const legacy = debts.filter(d => d.type === 'credit');
  const Card1 = (d: any) => {
    const principal = Number(d.principal) || 0, rem = Number(d.remainingBalance) || 0;
    const done = principal > 0 ? ((principal - rem) / principal) * 100 : 0;
    return (
      <Card key={d.id} className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="flex flex-wrap items-center gap-2 font-medium">{d.lender}{d.status === 'overdue' && <Pill tone="bad">Quá hạn</Pill>}{d.status === 'paid' && <Pill tone="good">Đã trả hết</Pill>}{d.creditCardId && <Pill tone="neutral">Trong dư nợ thẻ</Pill>}</p>
            <p className="num text-sm text-muted-foreground">{hasInterestRate(d.interestRate) ? (Number(d.interestRate) === 0 ? 'Không lãi' : `${d.interestRate}%/năm`) : 'Chưa nhập lãi suất'}{d.monthlyPayment ? `, trả ${formatCompact(d.monthlyPayment)}/tháng` : ''}{dm(d.dueDate) ? `, hạn ${dm(d.dueDate)}` : ''}</p>
          </div>
          <p className="text-right"><Money value={rem} size="row" /><span className="num block text-sm text-muted-foreground">trên {formatCompact(principal)}</span></p>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-muted" role="img" aria-label={`Đã trả ${Math.round(done)}%`}><div className="h-full rounded-full bg-good" style={{ width: `${done}%` }} /></div>
        <div className="flex flex-wrap gap-2">
          {rem > 0 && <Button size="sm" onClick={() => open({ kind: 'payDebt', id: d.id })}><Banknote size={14} aria-hidden /> Trả nợ</Button>}
          <Button size="sm" variant="outline" onClick={() => open({ kind: 'schedule', id: d.id })}><ListOrdered size={14} aria-hidden /> Lịch trả & lịch sử</Button>
          {rem > 0 && <Button size="sm" variant="outline" onClick={() => open({ kind: 'loanRate', id: d.id })}><Percent size={14} aria-hidden /> Cập nhật lãi suất</Button>}
          <Button size="sm" variant="outline" onClick={() => open({ kind: 'editLoan', id: d.id })}><Pencil size={14} aria-hidden /> Sửa thông tin</Button>
        </div>
      </Card>
    );
  };
  return (
    <div className="space-y-4">
      <div><Button size="sm" onClick={() => open({ kind: 'addLoan' })}><Plus size={14} aria-hidden /> Thêm khoản vay</Button></div>
      {live.length === 0 && <Card className="p-6 text-center text-muted-foreground">Không có khoản vay nào đang trả.</Card>}
      <div className="grid gap-3 md:grid-cols-2">{live.map(Card1)}</div>
      {paid.length > 0 && (
        <details className="group"><summary className="min-h-9 cursor-pointer py-1 text-muted-foreground hover:text-foreground">Đã trả hết ({paid.length})</summary>
          <div className="mt-3 grid gap-3 md:grid-cols-2">{paid.map(Card1)}</div></details>
      )}
      {legacy.length > 0 && <p className="text-sm text-muted-foreground">{legacy.length} bản ghi di sản (loại "thẻ") không tính vào nợ vay — dư nợ thẻ đã nằm ở tab Ví & thẻ.</p>}
    </div>
  );
}

// ───────── Trang ─────────
export default function AssetsDebts() {
  const ready = useCoreReady();
  const [tab, setTab] = useState<Tab>('sheet');
  const sheet = useAssetsUI(s => s.sheet);
  const { rows: payments, reload } = useDebtPayments();

  return (
    <div className="space-y-5">
      <PageHeader title="Tài sản & Nợ" question="Tôi có gì, nợ gì?" />
      <Tabs value={tab} onValueChange={v => setTab(v as Tab)}>
        <TabsList className="flex w-full max-w-lg justify-start sm:grid sm:grid-cols-4">{TABS.map(t => <TabsTrigger key={t.id} value={t.id}>{t.label}</TabsTrigger>)}</TabsList>
      </Tabs>

      {!ready ? <div className="space-y-3"><Skeleton className="h-36" /><Skeleton className="h-24" /></div> : (
        <>
          {tab === 'sheet' && <BalanceSheetTab />}
          {tab === 'wallets' && <WalletsTab />}
          {tab === 'loans' && <LoansTab payments={payments} />}
          {tab === 'reconcile' && <ReconcileTab debtPayments={payments} active />}
        </>
      )}

      {sheet?.kind === 'adjust' && <AdjustSheet id={sheet.id} />}
      {sheet?.kind === 'transfer' && <TransferSheet />}
      {sheet?.kind === 'payCard' && <PayCardSheet id={sheet.id} />}
      {sheet?.kind === 'addWallet' && <AddWalletSheet />}
      {sheet?.kind === 'editWallet' && <EditWalletSheet id={sheet.id} debtPayments={payments} />}
      {sheet?.kind === 'addLoan' && <AddLoanSheet onSaved={reload} />}
      {sheet?.kind === 'editLoan' && <EditLoanSheet id={sheet.id} onSaved={reload} />}
      {sheet?.kind === 'loanRate' && <LoanRateSheet id={sheet.id} payments={payments} onSaved={reload} />}
      {sheet?.kind === 'payDebt' && <PayDebtSheet id={sheet.id} onSaved={reload} />}
      {sheet?.kind === 'schedule' && <ScheduleSheet id={sheet.id} payments={payments} />}
    </div>
  );
}
